import type { ReactNode } from 'react'
import { Button, CloseButton, Drawer, HStack, Input, Portal, Text, VStack } from '@chakra-ui/react'
import type { StatusFilter, StreetSide } from '../../../lib/walkStops'
import type { ViewMode } from '../../../lib/walkPrefs'

const STATUS: { value: StatusFilter; label: string }[] = [
  { value: 'remaining', label: 'Remaining' }, { value: 'revisit', label: 'Revisit' }, { value: 'all', label: 'All' },
]
const SIDES: { value: StreetSide; label: string }[] = [
  { value: 'all', label: 'Both sides' }, { value: 'odd', label: 'Odd' }, { value: 'even', label: 'Even' },
]
const VIEWS: { value: ViewMode; label: string }[] = [
  { value: 'households', label: 'Households' }, { value: 'contacts', label: 'Contacts' }, { value: 'byStreet', label: 'By street' },
]

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <Button size="sm" minH="44px" borderRadius="full" aria-pressed={active} onClick={onClick}
      variant="outline" borderColor={active ? 'accent' : 'border'}
      bg={active ? 'accent.soft' : 'surface'} color={active ? 'accent' : 'fg'}>
      {children}
    </Button>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <VStack align="stretch" gap={2}>
      <Text fontSize="xs" fontWeight="bold" color="fg.muted" textTransform="uppercase" letterSpacing="wider">{title}</Text>
      {children}
    </VStack>
  )
}

/** [Spec §4.2] Bottom sheet behind the strip's filter chip (the SheetSelect drawer pattern). */
export function WalkFilterSheet({
  open, onClose, filter, onFilterChange, counts, viewMode, onViewModeChange,
  searchText, onSearchTextChange, side, onSideChange,
}: {
  open: boolean; onClose: () => void
  filter: StatusFilter; onFilterChange: (f: StatusFilter) => void
  counts: Record<StatusFilter, number>
  viewMode: ViewMode; onViewModeChange: (v: ViewMode) => void
  searchText: string; onSearchTextChange: (q: string) => void
  side: StreetSide; onSideChange: (s: StreetSide) => void
}) {
  return (
    <Drawer.Root open={open} placement="bottom" onOpenChange={(e) => { if (!e.open) onClose() }}>
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content borderTopRadius="xl" maxH="85vh" pb="env(safe-area-inset-bottom, 0px)" shadow="popover">
            <Drawer.CloseTrigger asChild><CloseButton size="sm" minW="44px" minH="44px" /></Drawer.CloseTrigger>
            <Drawer.Header borderBottomWidth="1px" borderColor="border">
              <Drawer.Title>Filters</Drawer.Title>
            </Drawer.Header>
            <Drawer.Body>
              <VStack align="stretch" gap={5} py={3}>
                <Section title="Show">
                  <HStack gap={2} wrap="wrap">
                    {STATUS.map(s => (
                      <Chip key={s.value} active={filter === s.value} onClick={() => onFilterChange(s.value)}>
                        {`${s.label} (${counts[s.value]})`}
                      </Chip>
                    ))}
                  </HStack>
                </Section>
                <Section title="Side of street">
                  <HStack gap={2} wrap="wrap">
                    {SIDES.map(s => (
                      <Chip key={s.value} active={side === s.value} onClick={() => onSideChange(s.value)}>{s.label}</Chip>
                    ))}
                  </HStack>
                </Section>
                <Input aria-label="Search name, address or workplace" placeholder="Search name, address or workplace"
                  minH="44px" value={searchText} onChange={(e) => onSearchTextChange(e.target.value)} />
                <Section title="View">
                  <HStack gap={2} wrap="wrap">
                    {VIEWS.map(v => (
                      <Chip key={v.value} active={viewMode === v.value} onClick={() => onViewModeChange(v.value)}>
                        {v.label}
                      </Chip>
                    ))}
                  </HStack>
                </Section>
              </VStack>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}
